import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {RouterLink} from "@angular/router";
import {TranslatePipe} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {OrgDto} from "../../repositories/dto/org.dto";
import {OrgLifecycleDto} from "../../repositories/dto/org-lifecycle.dto";
import {OrgLifecycle} from "../../libs/enum/org-lifecycle.enum";
import {AccDate} from "../../libs/pipes/date.pipe";
import {IconComponent} from "../../libs/icon/icon.component";
import {StateService} from "../../services/state/state.service";
import {PermissionService} from "../../services/auth/permission.service";
import {Permissions} from "../../services/auth/permissions.item";
import {isDefined, unsubscribe} from "../../libs/utils/epic.utils";
import {Paths} from "../../paths";

/**
 * Der Hinweis über allen Seiten, solange der Vertrag beendet ist und das Exportfenster läuft.
 *
 * 🔑 **Warum es ihn gibt.** Nach dem Vertragsende liefert das Backend die Rechte abgewertet aus — die Schaltflächen
 * zum Schreiben verschwinden von selbst. Ohne diesen Hinweis wirkt das wie ein Fehler: Der Auftraggeber sieht, dass
 * etwas fehlt, aber nicht warum. Anlage 4 Abschnitt 4 verzichtet für diese Stufe bewusst auf eine Vorwarnung, und
 * zwar mit der Begründung *„das Vertragsende ist bekannt"* — die trägt nur, solange das Gesehene mit diesem Datum
 * verbunden werden kann.
 *
 * ⚠️ **Stufe 1 und 2, nicht Stufe 3.** Seit § 10 Abs. 3 Ansehen und Export über die vollen 90 Tage zusagt, wirken beide
 * gleich und brauchen denselben Satz. Erst ab Tag 91 verweigert das Backend `GET /org/{id}`; dann kommt keine
 * Organisation mehr an, die einen Hinweis tragen könnte — dieser Zustand braucht eine eigene Antwort.
 *
 * ⚠️ **Alle Fristen kommen fertig aus dem Backend.** Hier wird nichts gerechnet — siehe `OrgLifecycleDto`.
 */
@Component({
  selector: 'acc-lifecycle-banner',
  imports: [
    TranslatePipe,
    AccDate,
    IconComponent,
    RouterLink
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './lifecycle-banner.component.html'
})
export class LifecycleBannerComponent implements OnInit, OnDestroy {

  protected readonly exportsPath: string = '/' + Paths.EXPORTS;

  protected org: OrgDto | undefined;

  /**
   * ⚠️ Der Verweis auf den Export wird mitgeprüft, nicht nur angezeigt. Der Menüpunkt dorthin hängt an `PermissionScope.EXPORT`
   * (`nav-org.ts`) — wer die drei Exportrechte nicht hat, sieht ihn nicht. Ein Hinweis, der ihn trotzdem dorthin schickt, endet
   * auf einer leeren Seite, und das ist genau der Eindruck, den dieses Banner verhindern soll.
   */
  protected perms: Permissions;

  private readonly stateService: StateService = inject(StateService);

  private readonly permissionService: PermissionService = inject(PermissionService);

  private readonly subscriptions: Subscription[] = [];

  constructor() {
    this.perms = this.permissionService.getPerms();
  }

  public ngOnInit(): void {
    this.subscriptions.push(this.stateService.getOrg().subscribe((org) => this.org = org));
    this.subscriptions.push(this.permissionService.accChanged().subscribe((p) => this.perms = p));
  }

  public ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  /**
   * Der Zustand, wenn er angezeigt gehört — sonst `undefined`. Gibt den Wert zurück statt eines `boolean`, damit
   * `@if (…; as lifecycle)` ihn im Template verengen kann; sonst hält die strenge Prüfung jeden Zugriff für möglicherweise leer.
   *
   * ⚠️ Beide Stufen, nicht nur die erste: Seit § 10 Abs. 3 lesenden Zugriff und Export über die vollen 90 Tage zusagt, wirken
   * `EXPORT_WINDOW` und `BLOCKED` gleich — und beide brauchen denselben Satz.
   */
  protected getEndedContract(): OrgLifecycleDto | undefined {
    const lifecycle: OrgLifecycleDto | undefined = this.org?.lifecycle;
    return isDefined(lifecycle) && (lifecycle.state === OrgLifecycle.EXPORT_WINDOW || lifecycle.state === OrgLifecycle.BLOCKED)
      ? lifecycle : undefined;
  }
}
